/** Password sign-in. Same flow and same error vocabulary as the POS, MUI markup. */

import { useState, type SyntheticEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { ErrorCode, isApiError, signedIn, useLoginMutation } from '@pos/shared';

import { useAppDispatch } from '@/app/hooks';

type LocationState = { from?: string } | null;

export function PasswordLoginForm({ username: initial = '' }: { username?: string }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [login, { isLoading, error }] = useLoginMutation();
  const [username, setUsername] = useState(initial);
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      const session = await login({ username: username.trim(), password }).unwrap();

      dispatch(signedIn(session));
      const from = (location.state as LocationState)?.from;
      void navigate(from ?? '/', { replace: true });
    } catch {
      // The hook holds the error; the Alert below renders it.
    }
  };

  /**
   * Marks both fields, never one. Which of the two was wrong is deliberately not said — the
   * server does not distinguish them either, and a form that narrows it down tells someone
   * probing accounts which usernames exist.
   */
  const rejected = isApiError(error) && error.code === ErrorCode.InvalidCredentials;

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <Stack spacing={2.5}>
        {error !== undefined && (
          <Alert severity="error" variant="outlined" sx={{ borderRadius: 'var(--pos-radius-md)' }}>
            {messageFor(error)}
          </Alert>
        )}

        <TextField
          label="Username"
          value={username}
          autoComplete="username"
          error={rejected}
          required
          fullWidth
          // The returning user arrives with their username already filled from the session hint,
          // so the caret belongs in the field they actually have to type in.
          {...(initial.length === 0 ? { autoFocus: true } : {})}
          onChange={(event) => {
            setUsername(event.target.value);
          }}
        />

        <TextField
          label="Password"
          type={revealed ? 'text' : 'password'}
          value={password}
          autoComplete="current-password"
          error={rejected}
          required
          fullWidth
          {...(initial.length > 0 ? { autoFocus: true } : {})}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={revealed ? 'Hide password' : 'Show password'}>
                    <IconButton
                      aria-label={revealed ? 'Hide password' : 'Show password'}
                      edge="end"
                      size="small"
                      onClick={() => {
                        setRevealed((current) => !current);
                      }}
                    >
                      {revealed ? (
                        <VisibilityOffOutlinedIcon fontSize="small" />
                      ) : (
                        <VisibilityOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            },
          }}
        />

        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          // The form-dialog control height, so the button and the fields above it read as one
          // column rather than a row of inputs with a smaller thing under them.
          sx={{ minHeight: 'var(--pos-density-control-height-comfortable)', fontSize: 16 }}
          disabled={isLoading || username.trim().length === 0 || password.length === 0}
          // Conditional spread, not a possibly-undefined prop: under exactOptionalPropertyTypes
          // "absent" and "undefined" are different types, and MUI's prop accepts only the first.
          {...(isLoading ? { startIcon: <CircularProgress size={16} color="inherit" /> } : {})}
        >
          {isLoading ? 'Signing in…' : 'Sign in'}
        </Button>

        {/*
          Not a "Forgot password?" link: this product has no self-service reset. Passwords are
          reset by an owner or manager from Users, so that is what the line says. A link to a
          recovery flow that does not exist is the kind of decoration that makes a product feel
          fake the first time somebody clicks it.
        */}
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
          Forgotten your password? An owner or manager can reset it for you.
        </Typography>
      </Stack>
    </form>
  );
}

function messageFor(error: unknown): string {
  if (!isApiError(error)) return 'Sign-in failed. Check the connection and try again.';

  switch (error.code) {
    case ErrorCode.InvalidCredentials:
      return 'That username or password is not right. Check both and try again.';
    case ErrorCode.AccountDeactivated:
      return 'This account has been switched off. Ask the owner to turn it back on.';
    case ErrorCode.Offline:
      return 'No connection to the server. Sign-in needs the network.';
    default:
      return error.message;
  }
}
